import React, { useContext, useEffect } from 'react';
import {
    AccordionStyled,
    AsideDrawer,
    FooterWrapper,
    MenuContent,
    SupportIcon,
} from './AsideMenuStyled';
import { AccordionDetails, AccordionSummary, Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AUTH_TYPE } from '@/lib/types/generalTypes';
import { setIsAsideMenu } from '@/state/general/generalSlice';
import { useDispatch, useSelector } from 'react-redux';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { logoutMember } from '@/state/member/useMember';
import { PrivateRouteContext } from '@/context/PrivateRouteContext';
import { RootState } from '@/store/store';
import { selectMemberProfile } from '@/state/member/memberSelectors';
import { matchPath, useLocation, useNavigate } from 'react-router';
import HyperTrackerFooter from '@/components/Hypertracker/HyperTrackerFooter';
import { selectIsAsideMenu } from '@/state/general/generalSelectors';
import Header from '@/components/Hypertracker/Header';
import { segmentIdToLabel } from '@/lib/constants/hyperTrackerConstants';
import { MODAL_TYPE_HANDLER } from '@/lib/types/hypertrackerTypes';
import { ROUTES } from '@/lib/constants';
import { setModalWindow } from '@/state/hypertracker/hypertackerSlice';
import useIsMemberAuthorized from '@/hooks/useIsMemberAuthorized';
import SecondaryHeaderMobile from '@/components/Hypertracker/SecondaryHeader/SecondaryHeaderMobile';
import ConnectionsList from '@/components/Hypertracker/HypertrackerSidebar/ConnectionsList';
import Button from '@mui/material/Button';
import LoginIcon from '@mui/icons-material/Login';
import useSegmentsData from '@/hooks/useSegmentsData';
import useMenuList from '@/components/Hypertracker/AsideMenu/useMenuList';
import AsideMenuDividersGroupNames from '@/components/Hypertracker/AsideMenu/AsideMenuDividersGroupNames';
import AsideMenuItem from '@/components/Hypertracker/AsideMenu/AsideMenuItem';
import AsideMenuAlphafeedItem from '@/components/Hypertracker/AsideMenu/AsideMenuAlphafeedItem';
import AsideMenuSubItem from '@/components/Hypertracker/AsideMenu/AsideMenuSubItem';
import AccountType from '@/components/Hypertracker/AsideMenu/AccountType';
import useConsumerDataQuery from '@/pages/HypertrackerApiDashboard/useConsumerDataQuery';
import useZopimChat from '@/hooks/useZopimChat';

const AsideMenu: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation('translation');
    const dispatch = useDispatch();
    const [expanded, setExpanded] = React.useState<string | false>(false);
    const { isReturnBack } = useContext(PrivateRouteContext);
    const member = useSelector((state: RootState) => selectMemberProfile(state));
    const isAsideMenu = useSelector((state: RootState) => selectIsAsideMenu(state));
    const container = window !== undefined ? () => window.document.body : undefined;
    const isMemberAuthorized = useIsMemberAuthorized();
    const { segmentsSummaryData } = useSegmentsData();
    const location = useLocation();
    const theme = useTheme();
    const { openChat } = useZopimChat();

    const { consumer } = useConsumerDataQuery();
    const hasApiToken = isMemberAuthorized && !!consumer?.accessToken;

    const closeMenu = () => {
        dispatch(setIsAsideMenu(false));
    };

    const handleSupportClick = () => {
        closeMenu();
        openChat();
    };

    const onMenuItemClick = (menuItem) => {
        const route = menuItem.url;
        if (route.includes(':id')) return;
        if (!route) return;
        if (!isMemberAuthorized && menuItem.label === 'menu_favorites') {
            handleOpenModalClick(AUTH_TYPE.SIGN_IN);
            return;
        }
        navigate(route);
        closeMenu();
    };

    const menuItems = useMenuList({ member, hasApiToken });

    const defineIsSelected = (url: string | null) => {
        if (!url) return false;
        const isDynamic = url.includes(':');

        return Boolean(matchPath({ path: url, end: !isDynamic }, location.pathname));
    };

    const handleChange =
        (panel: string, isSub) => (event: React.SyntheticEvent, isExpanded: boolean) => {
            if (!isSub) return;
            setExpanded(isExpanded ? panel : false);
        };

    useEffect(() => {
        if (isAsideMenu) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [isAsideMenu]);

    const handleOpenModalClick = (value) => {
        dispatch(
            setModalWindow({
                modalType: MODAL_TYPE_HANDLER.IS_AUTH_MODAL,
                authorizationModalType: value,
            }),
        );
    };

    const onElementClick = (menuItem) => {
        if (menuItem.url === 'logout') {
            isReturnBack(false);
            logoutMember(true)
                .then(() => {
                    console.log('ok');
                })
                .catch((error) => {
                    console.log('err', error);
                    return;
                });

            return;
        }

        if (menuItem.isExternal && menuItem.url) {
            const newWindow = window.open();
            if (newWindow) {
                newWindow.location = menuItem.url;
            }
            return;
        }

        if (!menuItem.isExternal && menuItem.url) {
            navigate(menuItem.url);
            closeMenu();
        }
    };

    const onSegmentClick = (segment) => {
        dispatch(setIsAsideMenu(false));
        navigate(`${ROUTES.HYPERTRACKER_SEGMENTS}/${segmentIdToLabel?.[+segment]}`);

        setExpanded(false);
    };

    return (
        <AsideDrawer
            container={container}
            variant="temporary"
            open={isAsideMenu}
            onClose={closeMenu}
            ModalProps={{
                keepMounted: false,
            }}
        >
            <Header />
            {isMemberAuthorized && <AccountType closeMenu={closeMenu} />}

            {isAsideMenu && <SecondaryHeaderMobile segmentsData={segmentsSummaryData} />}
            <MenuContent
                sx={{
                    justifyContent: 'space-between',
                    flexGrow: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                }}
            >
                <Stack direction={'column'} gap={'4px'} width={'100%'}>
                    {menuItems.map((menuItem, index) => {
                        const isSelected = defineIsSelected(menuItem.url);
                        const isSub = menuItem.subMenu.length > 0;

                        if (menuItem.label === 'menu_alphafeed') {
                            return (
                                <React.Fragment key={`${menuItem.id}_${index}_fragment`}>
                                    <AsideMenuAlphafeedItem t={t} closeMenu={closeMenu} />
                                </React.Fragment>
                            );
                        }

                        return (
                            <React.Fragment key={`${menuItem.id}_${index}_fragment`}>
                                <AsideMenuDividersGroupNames
                                    menuItem={menuItem}
                                    t={t}
                                    theme={theme}
                                />
                                <AccordionStyled
                                    key={`${menuItem.id}_${index}`}
                                    disableGutters
                                    elevation={0}
                                    expanded={expanded === `panel${index}`}
                                    onChange={handleChange(`panel${index}`, isSub)}
                                    onClick={() => (isSub ? () => {} : onMenuItemClick(menuItem))}
                                    sx={{
                                        backgroundColor:
                                            menuItem.id === 'menu_api'
                                                ? theme.palette.primitives.woodsmoke[900]
                                                : isSelected
                                                  ? 'rgba(255,255,255,0.05)'
                                                  : 'transparent',
                                        boxShadow: 'none',
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={
                                            isSub ? (
                                                <ChevronRightIcon
                                                    sx={{
                                                        color: theme.palette.primitives
                                                            .woodsmoke[500],
                                                    }}
                                                />
                                            ) : null
                                        }
                                        aria-controls={`panel${index}bh-content`}
                                        id={`panel${index}bh-header`}
                                        sx={{
                                            minHeight: '32px',
                                            padding: '8px',
                                            '& .MuiAccordionSummary-content': { margin: 0 },
                                        }}
                                    >
                                        <AsideMenuItem
                                            menuItem={menuItem}
                                            t={t}
                                            isSelected={isSelected}
                                        />
                                    </AccordionSummary>
                                    {isSub && (
                                        <AccordionDetails>
                                            {menuItem.subMenu.map((group) => {
                                                return (
                                                    <AsideMenuSubItem
                                                        key={group.groupLabel || 'avatar_group'}
                                                        group={group}
                                                        t={t}
                                                        onElementClick={onElementClick}
                                                        onSegmentClick={onSegmentClick}
                                                    />
                                                );
                                            })}
                                        </AccordionDetails>
                                    )}
                                </AccordionStyled>
                            </React.Fragment>
                        );
                    })}
                </Stack>
                <Stack direction={'column'} gap={'12px'} width={'100%'}>
                    {isMemberAuthorized && <ConnectionsList setOpen={closeMenu} />}
                    {!isMemberAuthorized && (
                        <Stack
                            direction={'column'}
                            gap={'8px'}
                            sx={{
                                borderColor: (theme) => theme.palette.divider,
                                borderStyle: 'solid',
                                borderBottomWidth: '1px',
                                padding: '12px 0',
                            }}
                        >
                            <Button
                                variant={'contained'}
                                size={'small'}
                                color={'info'}
                                sx={{
                                    backgroundColor: theme.palette.primitives.riptide[300],
                                    padding: '8px 12px',
                                }}
                                onClick={() => handleOpenModalClick(AUTH_TYPE.SIGN_IN)}
                                startIcon={<LoginIcon />}
                            >
                                <Typography variant={'p12'} fontWeight={'600'}>
                                    {t('hypertracker_sidebar.sign_in')}
                                </Typography>
                            </Button>
                            <Button
                                variant={'contained'}
                                size={'small'}
                                color={'info'}
                                sx={{ backgroundColor: theme.palette.primitives.riptide[100] }}
                                onClick={handleSupportClick}
                                startIcon={<SupportIcon />}
                            >
                                <Typography
                                    variant={'p12'}
                                    fontWeight={'600'}
                                    mt={'6px'}
                                    mb={'6px'}
                                >
                                    {t('hypertracker_sidebar.support_label')}
                                </Typography>
                            </Button>
                        </Stack>
                    )}
                    {isMemberAuthorized && (
                        <Button
                            variant={'contained'}
                            size={'small'}
                            color={'info'}
                            sx={{
                                backgroundColor: theme.palette.primitives.riptide[100],
                                marginBottom: '12px',
                            }}
                            onClick={handleSupportClick}
                            startIcon={<SupportIcon />}
                        >
                            <Typography variant={'p12'} fontWeight={'600'} mt={'6px'} mb={'6px'}>
                                {t('hypertracker_sidebar.support_label')}
                            </Typography>
                        </Button>
                    )}
                </Stack>
            </MenuContent>

            <FooterWrapper sx={{ flexDirection: 'column', flexShrink: 0, margin: '0 -8px' }}>
                <HyperTrackerFooter isFixed={false} />
            </FooterWrapper>
        </AsideDrawer>
    );
};

export default AsideMenu;
